import React, { useState, useEffect } from 'react';
import { X, MapPin, CheckCircle, AlertCircle, Loader2, Send } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import emailjs from '@emailjs/browser';

// Toast Notification Component
const Toast = ({ type, message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000); // Auto-close after 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-[60] animate-in slide-in-from-top-2 duration-300">
      <div className={`flex items-center p-4 rounded-lg shadow-lg max-w-md ${
        type === 'success' 
          ? 'bg-green-50 border border-green-200' 
          : 'bg-red-50 border border-red-200'
      }`}>
        {type === 'success' ? (
          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
        )}
        <p className={`text-sm font-medium flex-1 ${
          type === 'success' ? 'text-green-800' : 'text-red-800'
        }`}>
          {message}
        </p>
        <button
          onClick={onClose}
          className={`ml-3 text-gray-400 hover:text-gray-600 ${
            type === 'success' ? 'hover:text-green-600' : 'hover:text-red-600'
          }`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const ContactModal = () => {
  const { contactFormOpen, setContactFormOpen } = useAppContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '', isVisible: false });
  const [isSuccess, setIsSuccess] = useState(false);
  
  // EmailJS configuration - Using environment variables
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  
  // Check if environment variables are loaded
  if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
    console.error('EmailJS environment variables are not properly configured');
  }
  
  const showToast = (type, message) => {
    setToast({ type, message, isVisible: true });
  };
  
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const resetForm = () => {
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSuccess(false);
  };
  
  const closeModal = () => {
    setContactFormOpen(false);
    setTimeout(() => {
      resetForm();
      hideToast();
    }, 300); // Wait for modal close animation
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Check if environment variables are available
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      showToast('error', 'Email service is not properly configured. Please contact the administrator.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_name: 'EXCESSD Software',
        },
        EMAILJS_PUBLIC_KEY
      );
      
      console.log('Email sent successfully:', result.text);
      setIsSuccess(true);
      showToast('success', 'Thank you for contacting us! We will get back to you shortly.');
      
      // Auto-close modal after success
      setTimeout(() => {
        closeModal();
      }, 2500);
      
    } catch (error) {
      console.error('Error sending email:', error);
      showToast('error', 'Sorry, there was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!contactFormOpen) return null;
  
  return (
    <>
      {/* Toast Notification */}
      <Toast 
        type={toast.type}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div 
          className="absolute inset-0 bg-black bg-opacity-60 transition-opacity duration-300" 
          onClick={closeModal}
        ></div>
        
        <div className="relative w-full max-w-5xl flex flex-col md:flex-row bg-white rounded-lg overflow-hidden transform transition-all duration-300 scale-100">
          {/* Left side - Contact info */}
          <div className="w-full md:w-1/3 bg-blue-900 bg-gradient-to-br from-blue-900 to-blue-700 p-8 text-white relative">
            {isSuccess && (
              <div className="absolute inset-0 bg-green-600 bg-opacity-90 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle size={64} className="mx-auto mb-4 text-green-200" />
                  <h3 className="text-2xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-green-100">We'll be in touch soon.</p>
                </div>
              </div>
            )}
            
            <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
            <p className="mb-8">Interested in exploring opportunities?</p>
            
            <div className="mb-8">
              <div className="flex items-start mb-2">
                <MapPin size={20} className="mr-2 mt-1" />
                <div>
                  <h3 className="font-bold mb-1">Find us</h3>
                  <p className="text-sm">
                    Lucknow,<br />
                    Uttar Pradesh,<br />
                    India<br />
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Contact form */}
          <div className="w-full md:w-2/3 p-6 relative">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-yellow-500">Contact Us</h3>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Your Name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isSubmitting || isSuccess}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all disabled:bg-gray-100"
                  />
                </div>
                
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Your Email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting || isSuccess}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all disabled:bg-gray-100"
                  />
                </div>
              </div>
              
              <div>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder="Subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSubmitting || isSuccess}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Your Message"
                  rows="4"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting || isSuccess}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all disabled:bg-gray-100 resize-none"
                ></textarea>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting || isSuccess}
                  className="bg-yellow-400 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-500 transition-all uppercase disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : isSuccess ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactModal;